import React, { useState } from 'react'
import { useNewTranslation } from 'hooks/index'
import { Flex, Box } from 'rebass'

import { theme } from '../../../../theme'
import Tab, { TabOption } from 'particles/Tab'
import styled from 'particles/themes/styled'

export type DecisionEditorStates = 'Reject' | 'Request edits' | 'Approve'

interface BaseComponentProps {
    renderApproval: any
    renderRequestEdits: any
    renderRejection: any
    disableApprove?: boolean
    disableRequestEdit?: boolean
    [key: string]: any
}

const withDecisionEditor = (BaseComponent: React.FC<any>) =>
    function DecisionEditor({
        renderApproval,
        renderRequestEdits,
        renderRejection,
        disableApprove,
        disableRequestEdit,
        ...props
    }: BaseComponentProps) {
        const [newT] = useNewTranslation()
        const [decisionEditorState, setDecisionEditorState] = useState<{
            state: DecisionEditorStates | undefined
            data: any | undefined
        }>({ state: undefined, data: undefined })

        const handleDecisionEditorState = (state: DecisionEditorStates, data: any, callback: any) => {
            setDecisionEditorState({ state, data })
            if (callback) callback()
        }

        const handleClear = (): void => {
            setDecisionEditorState({ state: undefined, data: undefined })
        }

        const tabOptions: TabOption[] = [
            {
                id: 'Approve',
                label: newT('button.approve'),
                disabled: disableApprove,
                onClick: () => setDecisionEditorState({ state: 'Approve', data: decisionEditorState.data }),
            },
            {
                id: 'Request edits',
                label: newT('button.requestEdits'),
                // disable tab if step ended
                disabled: disableRequestEdit,
                onClick: () => setDecisionEditorState({ state: 'Request edits', data: decisionEditorState.data }),
            },
            {
                id: 'Reject',
                label: newT('button.reject'),
                onClick: () => setDecisionEditorState({ state: 'Reject', data: decisionEditorState.data }),
            },
        ]

        const getElement = () => {
            const elementType = decisionEditorState.state
            if (elementType === 'Approve') return renderApproval(decisionEditorState.data, handleClear)
            if (elementType === 'Request edits') return renderRequestEdits(decisionEditorState.data, handleClear)
            if (elementType === 'Reject') return renderRejection(decisionEditorState.data, handleClear)
        }

        return (
            <Flex>
                <Box flex={1} bg={theme.newColors.neutral50}>
                    <BaseComponent
                        decisionEditorState={decisionEditorState}
                        setDecisionEditorState={handleDecisionEditorState}
                        {...props}
                    />
                </Box>
                {decisionEditorState.state != undefined && (
                    <Box bg={theme.newColors.neutral100} width="300px">
                        <Box bg={theme.newColors.neutral50} height="100%" mx={theme.spacing.xxs} px={theme.spacing.xxs}>
                            <Tab tabs={tabOptions} selectedTab={decisionEditorState.state} />
                            <ElementContainer>{getElement()}</ElementContainer>
                        </Box>
                    </Box>
                )}
            </Flex>
        )
    }

// Temporary container while waiting for this component to be redesigned
const ElementContainer = styled.div`
    padding-top: 12px;
`

export default withDecisionEditor
